import React, { useEffect, useRef, useState } from "react"
import Loader from "react-loaders"
import { useDispatch, useSelector } from "react-redux"
import {
  Card,
  CardBody,
  Col,
  Container,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  Label,
  Row,
} from "reactstrap"
import api from "services/api.service"
import { userService } from "services/user.service"
import colors from "utils/colors"
import { currencyFormatter } from "utils/formatters"
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import BarChartModal from "./BarChartModal"
import anychart from "anychart"
import RadarChartModal from "./RadarChartModal"
import AltH2hChart from "./AltH2hChartModal"
import { swalWithConfirmButton } from "components/custom/swal"
import AltScatterChartModal from "./AltScatterChartModal"
import { changeNavbarParams, loadPageItem } from "store/actions"
import BubbleChart from "../SetBubbleChart/BubbleChart"
import { ObjFinAlert } from "services/scenario.service"
import { mapAllPropsToLowercase } from "helpers/objPropsToLowercaseMapper"
import useModal from "hooks/useModalHook"
import ConfigModal from "pages/Projects/Projects/ConfigModal"
import ProjectsContext from "pages/Projects/Projects/Context/ProjectsContext"
import FinancialChartModal from "./FinancialChartModal"
import SyncfusionGrid from "components/custom/Syncfusion/SyncfusionGrid"

const Charts = props => {
  const currentUser = userService.getLoggedInUser()
  const scenarioId = props.match.params.scenarioId
  const dispatch = useDispatch()
  const { pageItem } = useSelector(state => ({
    pageItem: state.pageItem.pageItem,
  }))
  const { optObjFin } = useSelector(state => ({
    optObjFin: state.optObjFin.optObjFin,
  }))
  var gridRef = useRef(null)

  const [isLoading, setIsLoading] = useState(false)
  const [objectivesDropdownList, setObjectivesDropdownList] = useState([])
  const [selectedObj, setSelectedObj] = useState(0)
  const [showActionsDropdown, setShowActionsDropdown] = useState(false)
  const [showChartsDropdown, setShowChartsDropdown] = useState(false)
  const [gridData, setGridData] = useState([])
  const [altIdsList, setAltIdsList] = useState([])
  const [hasSecondaryScores, setHasSecondaryScores] = useState(true)

  const [isBarChartModalVisible, setIsBarChartModalVisible] = useState(false)
  const toggleBarChartModal = () =>
    setIsBarChartModalVisible(!isBarChartModalVisible)
  const [isRadarChartModalVisible, setIsRadarChartModalVisible] =
    useState(false)
  const toggleRadarChartModal = () =>
    setIsRadarChartModalVisible(!isRadarChartModalVisible)
  const [isAltH2hChartModalVisible, setIsAltH2hChartModalVisible] =
    useState(false)
  const toggleAltH2hChartModal = () =>
    setIsAltH2hChartModalVisible(!isAltH2hChartModalVisible)
  const [isScatterChartModalVisible, setIsScatterChartModalVisible] =
    useState(false)
  const toggleScatterChartModal = () =>
    setIsScatterChartModalVisible(!isScatterChartModalVisible)
  const [isBubbleChartModalVisible, setIsBubbleChartModalVisible] =
    useState(false)
  const toggleBubbleChartModal = () =>
    setIsBubbleChartModalVisible(!isBubbleChartModalVisible)
  const { show: showFinancialChartModal, toggle: toggleFinancialChartModal } =
    useModal()

  const [configItems, setConfigItems] = useState([])
  const [columns, setColumns] = useState([])

  const { show: showConfigModal, toggle: toggleConfigModal } = useModal()

  useEffect(() => {
    dispatch(
      loadPageItem({
        userId: currentUser.userID,
        scenarioId: scenarioId,
        alternativeId: 0,
        viewName: "AnalysisCharts",
      })
    )
    dispatch(
      changeNavbarParams({
        userId: currentUser.userID,
        scenarioId: scenarioId,
        viewName: "AnalysisCharts",
      })
    )
    anychart.licenseKey("definitiveinc.com-d85807d1-4c76171e")
  }, [])

  useEffect(() => {
    loadData()
  }, [optObjFin])

  useEffect(() => {
    if (configItems.length > 0 && gridData.length > 0) {
      setAltIdsList([])
      loadColumns()
    }
  }, [configItems, gridData])

  const loadData = async () => {
    try {
      setIsLoading(true)
      let cfg = await api.getConfigItems(
        currentUser.userID,
        scenarioId,
        "Charts"
      )
      setConfigItems(cfg)
      let scen = await api.getScenario(scenarioId)
      let objs = await api.getObjectives(scen.scenarioID)
      setObjectivesDropdownList([
        {
          value: 0,
          text: scen.scenarioName,
        },
        ...objs
          .filter(x => x.measures && x.measures.length > 0)
          .map(x => {
            return {
              value: x.objectiveID,
              text: x.objectiveName,
            }
          }),
      ])
      await loadGrid(selectedObj)
      setIsLoading(false)
    } catch (er) {
      console.log(er)
      setIsLoading(false)
    }
  }

  const loadGrid = async objId => {
    let { obj, fin } = optObjFin
    let data = await api.getAlternativesForCharts(
      scenarioId,
      objId,
      obj == null ? 0 : obj.objModelID,
      fin == null ? 0 : fin.finModelID
    )
    let hasSs = false
    for (var i = 0; i < data.length; i++) {
      if (data[i].secondaryScoreNumber > 0) {
        hasSs = true
        break
      }
    }

    setHasSecondaryScores(hasSs)
    setGridData(
      mapAllPropsToLowercase(enrichWithRunningCostAndBenefitFields(data))
    )
  }

  const enrichWithRunningCostAndBenefitFields = alts => {
    let copy = [...alts]
    for (let i = 0; i < copy.length; i++) {
      if (i == 0) {
        copy[i].runningCost = copy[i].cost
        copy[i].runningBenefit = copy[i].benefit
      } else {
        let prev = copy[i - 1]
        copy[i].runningCost = prev.runningCost + copy[i].cost
        copy[i].runningBenefit = prev.runningBenefit + copy[i].benefit
      }
    }
    return copy
  }

  const showAltNotSelectedAlert = () => {
    swalWithConfirmButton.fire({
      title: `To run the chart, you must select at least one project.`,
      icon: "warning",
      showCancelButton: false,
      confirmButtonText: "Ok",
    })
  }

  const loadColumns = () => {
    let cols = [
      {
        headerText: "ID",
        field: "alternativenum",
        width: 80,
        freeze: "Left",
      },
      {
        headerText: "Project",
        field: "alternative",
        width: 400,
        freeze: "Left",
        clipMode: "EllipsisWithTooltip",
      },
      {
        headerText: "Version",
        field: "version",
        alignText: "Right",
        width: 100,
        freeze: "Left",
        filter: {
          type: "Excel",
          itemTemplate: data => {
            return (
              <div
                style={{ lineHeight: "20px" }}
                dangerouslySetInnerHTML={{
                  __html: data.version,
                }}
              ></div>
            )
          },
        },
        template: data => (
          <div
            style={{ lineHeight: "20px" }}
            dangerouslySetInnerHTML={{
              __html: data.version,
            }}
          ></div>
        ),
      },
      {
        headerText: "Excluded",
        field: "excludestring",
        width: 100,
        template: data => {
          let val = data.excludestring
          if (val == undefined) {
            return ""
          }
          return <span style={{ color: "red" }}>{val}</span>
        },
      },
      {
        headerText: "Benefit Score",
        field: "saascore",
        width: 200,
        filter: {
          type: "Excel",
          itemTemplate: data => {
            return (
              <div
                style={{ lineHeight: "20px" }}
                dangerouslySetInnerHTML={{
                  __html: data.saascore,
                }}
              ></div>
            )
          },
        },
        template: data => (
          <div
            style={{ lineHeight: "20px" }}
            dangerouslySetInnerHTML={{
              __html: data.saascore,
            }}
          ></div>
        ),
      },
    ]

    if (hasSecondaryScores) {
      cols.push({
        headerText: "Risk Score",
        field: "secondaryscore",
        width: 200,
        filter: {
          type: "Excel",
          itemTemplate: data => {
            return (
              <div
                style={{ lineHeight: "20px" }}
                dangerouslySetInnerHTML={{
                  __html: data.secondaryscore,
                }}
              ></div>
            )
          },
        },
        template: data => (
          <div
            style={{ lineHeight: "20px" }}
            dangerouslySetInnerHTML={{
              __html: data.secondaryscore,
            }}
          ></div>
        ),
      })
    }

    cols.push({
      headerText: "Decision",
      field: "finaldecision",
      width: 150,
      filter: {
        type: "Excel",
        itemTemplate: data => {
          return (
            <div
              style={{ lineHeight: "20px" }}
              dangerouslySetInnerHTML={{
                __html: data.finaldecision,
              }}
            ></div>
          )
        },
      },
      template: data => (
        <div
          style={{ lineHeight: "20px" }}
          dangerouslySetInnerHTML={{
            __html: data.finaldecision,
          }}
        ></div>
      ),
    })

    configItems
      .filter(x => x.configType == "CB")
      .map(x => {
        let colParam = x.colParam.toLowerCase()
        if (colParam == "cost") {
          x.orderid = 1
        } else if (colParam == "cost_project") {
          x.orderid = 2
        } else if (colParam == "benefit") {
          x.orderid = 3
        } else if (colParam == "return") {
          x.orderid = 4
        } else if (colParam == "roi") {
          x.orderid = 6
        } else if (colParam == "irr") {
          x.orderid = 5
        } else if (colParam == "pbp") {
          x.orderid = 7
        } else if (colParam == "projectmanager") {
          x.orderid = 8
        } else if (colParam == "sponsor") {
          x.orderid = 9
        } else {
          x.orderid = 100
        }

        return x
      })
      .sort(function (a, b) {
        if (a.orderid < b.orderid) {
          return -1
        }
        if (a.orderid > b.orderid) {
          return 1
        }
        return 0
      })
      .forEach(ci => {
        if (ci.configItemValue == "1") {
          const colParam = ci.colParam.toLowerCase()
          let col = {
            headerText: ci.displayName,
            field: colParam == "return" ? "returnamount" : colParam,
            width: 150,
          }

          if (
            colParam == "return" ||
            colParam == "cost" ||
            colParam == "cost_project" ||
            colParam == "costreserve" ||
            colParam == "benefit" ||
            colParam == "roi" ||
            colParam == "irr" ||
            colParam == "pbp" ||
            colParam == "paybackyears"
          ) {
            col.textAlign = "Right"
          }

          if (
            colParam == "return" ||
            colParam == "cost" ||
            colParam == "cost_project" ||
            colParam == "benefit"
          ) {
            col.textAlign = "Right"
            col.template = data => {
              return currencyFormatter.format(data[col.field])
            }
          }
          cols.push(col)

          if (col.field == "cost") {
            let runningCostCol = {
              headerText: "Running Cost",
              field: "runningcost",
              width: 150,
              textAlign: "Right",
              template: data => {
                return currencyFormatter.format(data.runningcost)
              },
            }
            cols.push(runningCostCol)
          }
          if (col.field == "benefit") {
            let runningBenefitCol = {
              headerText: "Running Benefit",
              field: "runningbenefit",
              width: 150,
              textAlign: "Right",
              template: data => {
                return currencyFormatter.format(data.runningbenefit)
              },
            }
            cols.push(runningBenefitCol)
          }
        }
      })

    cols.push({
      headerText: "Decision Notes",
      field: "decisionnotes",
      width: 150,
      clipMode: "EllipsisWithTooltip",
    })

    if (scenarioId < 0) {
      cols.splice(2, 0, {
        headerText: "Model",
        field: "scenarioname",
        width: 250,
      })
    }

    setColumns(cols)
  }

  const onActionComplete = args => {
    if (args.requestType === "sorting" || args.requestType === "filtering") {
      updateRunningCost()
    }
  }

  const updateRunningCost = () => {
    const grid = gridRef.current
    if (!grid) return

    const visibleData = grid.getCurrentViewRecords() // only visible rows

    setGridData(
      mapAllPropsToLowercase(enrichWithRunningCostAndBenefitFields(visibleData))
    )
  }

  return (
    <React.Fragment>
      <ProjectsContext.Provider
        value={{
          bcStatusOptions: [],
          completeOptions: [],
          brmOptions: [],
          sponsorOptions: [],
          ldoOptions: [],
          ldoListOptions: [],
          pmOptions: [],
          deliveryOptions: [],
          submitterOptions: [],
          workflowOptions: [],
          planningOptions: [],
          investmentOptions: [],
          decisionOptions: [],
          programs: [],
        }}
      >
        <div className="page-content">
          <Container fluid>
            <Breadcrumbs
              title={pageItem !== undefined ? pageItem.pageSubTitle : ""}
            />
            <Card>
              <CardBody>
                {isLoading && (
                  <Loader
                    type="line-scale-pulse-out"
                    color={colors.primary}
                    style={{ textAlign: "center" }}
                  />
                )}
                {!isLoading && (
                  <>
                    <Row>
                      <Col
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "space-between",
                        }}
                      >
                        <div className="mb-3">
                          {objectivesDropdownList.length > 1 &&
                            scenarioId > 0 && (
                              <>
                                <Label className="form-label">View</Label>
                                <select
                                  className="form-control form-select select2 mb-xxl-0"
                                  value={selectedObj || 0}
                                  style={{ width: "400px" }}
                                  onChange={e => {
                                    setSelectedObj(e.target.value)
                                    loadGrid(e.target.value)
                                  }}
                                >
                                  {objectivesDropdownList.map((a, idx) => {
                                    return (
                                      <option key={idx} value={`${a.value}`}>
                                        {a.text}
                                      </option>
                                    )
                                  })}
                                </select>
                              </>
                            )}
                        </div>
                        <div
                          className="mb-3"
                          style={{ alignSelf: "end", display: "flex" }}
                        >
                          <Dropdown
                            // style={{ display: "inline-block" }}
                            isOpen={showActionsDropdown}
                            toggle={() =>
                              setShowActionsDropdown(!showActionsDropdown)
                            }
                          >
                            <DropdownToggle
                              tag="button"
                              className="btn btn-outline-primary save-user"
                            >
                              Actions <i className="mdi mdi-chevron-down" />
                            </DropdownToggle>
                            <DropdownMenu>
                              <DropdownItem
                                onClick={() => {
                                  toggleConfigModal()
                                }}
                              >
                                <i className="fas fa-cog"></i> Configure View
                              </DropdownItem>
                            </DropdownMenu>
                          </Dropdown>
                          <Dropdown
                            // style={{ display: "inline-block" }}
                            isOpen={showChartsDropdown}
                            toggle={() =>
                              setShowChartsDropdown(!showChartsDropdown)
                            }
                          >
                            <DropdownToggle
                              tag="button"
                              className="btn btn-outline-primary save-user"
                            >
                              Select Chart{" "}
                              <i className="mdi mdi-chevron-down" />
                            </DropdownToggle>
                            <DropdownMenu>
                              {scenarioId > 0 && (
                                <DropdownItem
                                  onClick={() => {
                                    if (altIdsList.length > 0) {
                                      toggleBarChartModal()
                                    } else {
                                      showAltNotSelectedAlert()
                                    }
                                  }}
                                >
                                  <i className="mdi mdi-chart-timeline"></i> Bar
                                  Chart
                                </DropdownItem>
                              )}

                              {hasSecondaryScores && (
                                <DropdownItem
                                  onClick={() => {
                                    if (altIdsList.length > 0) {
                                      toggleBubbleChartModal()
                                    } else {
                                      showAltNotSelectedAlert()
                                    }
                                  }}
                                >
                                  <i className="mdi mdi-chart-scatter-plot-hexbin"></i>{" "}
                                  Bubble Chart
                                </DropdownItem>
                              )}

                              <DropdownItem
                                onClick={() => {
                                  if (altIdsList.length > 0) {
                                    toggleFinancialChartModal()
                                  } else {
                                    showAltNotSelectedAlert()
                                  }
                                }}
                              >
                                <i className="mdi mdi-chart-line"></i> Financial
                                Chart
                              </DropdownItem>

                              {scenarioId > 0 && (
                                <DropdownItem
                                  onClick={() => {
                                    if (altIdsList.length == 2) {
                                      toggleAltH2hChartModal()
                                    } else {
                                      swalWithConfirmButton.fire({
                                        title: `For Head to Head chart you must select exactly 2 projects`,
                                        icon: "warning",
                                        showCancelButton: false,
                                        confirmButtonText: "Ok",
                                      })
                                    }
                                  }}
                                >
                                  <i className="mdi mdi-chart-gantt"></i> H2H
                                  Comparison
                                </DropdownItem>
                              )}

                              {scenarioId > 0 && (
                                <DropdownItem
                                  onClick={() => {
                                    if (altIdsList.length > 0) {
                                      toggleRadarChartModal()
                                    } else {
                                      showAltNotSelectedAlert()
                                    }
                                  }}
                                >
                                  <i className="mdi mdi-radar"></i> Radar Plot
                                </DropdownItem>
                              )}

                              <DropdownItem
                                onClick={() => {
                                  if (altIdsList.length > 0) {
                                    toggleScatterChartModal()
                                  } else {
                                    showAltNotSelectedAlert()
                                  }
                                }}
                              >
                                <i className="mdi mdi-chart-scatter-plot"></i>{" "}
                                Scatter Chart
                              </DropdownItem>
                            </DropdownMenu>
                          </Dropdown>
                        </div>
                      </Col>
                    </Row>
                    <ObjFinAlert optObjFin={optObjFin} />
                    <Row>
                      <Col>
                        {columns.length > 0 && (
                          <SyncfusionGrid
                            gridRef={gridRef}
                            columns={columns}
                            data={gridData}
                            allowPaging={false}
                            actionComplete={onActionComplete}
                            selectionSettings={{
                              type: "Multiple",
                              mode: "Row",
                              checkboxOnly: true,
                            }}
                            // sortSettings={{
                            //   columns: [{ field: "orderId", direction: "Ascending" }],
                            // }}
                          />
                        )}
                      </Col>
                    </Row>
                  </>
                )}
              </CardBody>
            </Card>
          </Container>
        </div>
        <BarChartModal
          scenarioId={scenarioId}
          alternativeList={altIdsList}
          objectiveId={selectedObj}
          isOpen={isBarChartModalVisible}
          toggleModal={toggleBarChartModal}
          optObjFin={optObjFin}
        />
        <RadarChartModal
          scenarioId={scenarioId}
          alternativeList={altIdsList}
          isOpen={isRadarChartModalVisible}
          toggleModal={toggleRadarChartModal}
          optObjFin={optObjFin}
        />
        <AltH2hChart
          scenarioId={scenarioId}
          alternativeList={altIdsList}
          isOpen={isAltH2hChartModalVisible}
          toggleModal={toggleAltH2hChartModal}
          optObjFin={optObjFin}
        />
        <AltScatterChartModal
          scenarioId={scenarioId}
          alternativeList={altIdsList}
          isOpen={isScatterChartModalVisible}
          toggleModal={toggleScatterChartModal}
          optObjFin={optObjFin}
        />
        <BubbleChart
          scenarioId={scenarioId}
          alternativeList={altIdsList}
          isOpen={isBubbleChartModalVisible}
          toggleModal={toggleBubbleChartModal}
          optObjFin={optObjFin}
        />

        <ConfigModal
          isOpen={showConfigModal}
          toggleModal={toggleConfigModal}
          reloadData={loadData}
          scenarioId={scenarioId}
          configType="Charts"
        />

        <FinancialChartModal
          isOpen={showFinancialChartModal}
          toggleModal={toggleFinancialChartModal}
          scenarioId={scenarioId}
          alternativeList={altIdsList}
        />
      </ProjectsContext.Provider>
    </React.Fragment>
  )
}

export default Charts
